import React, { useEffect, useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { api } from "../utils/api";
import { toast } from "sonner";
import { Loader2, AlertTriangle, CheckCircle2 } from "lucide-react";

export default function MagicLogin() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const navigate = useNavigate();
  const { magicLogin } = useAuth();

  const [status, setStatus] = useState<"loading" | "success" | "error">("loading");
  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    if (!token) {
      setStatus("error");
      setErrorMessage("No login token provided.");
      return;
    }

    const verify = async () => {
      try {
        const data = await api.get(`/api/auth/magic-link/verify?token=${encodeURIComponent(token)}`, {
          skipAuthHandler: true,
        });

        if (data?.success && data.token && data.user && data.permissions) {
          magicLogin(data.token, data.user, data.permissions);
          setStatus("success");
          toast.success("Welcome to Idarah!");
          setTimeout(() => navigate("/dashboard"), 1200);
        } else {
          setStatus("error");
          setErrorMessage(data?.message || "Invalid or expired link.");
        }
      } catch (err: any) {
        setStatus("error");
        setErrorMessage(err?.message || "Invalid or expired link.");
      }
    };

    verify();
  }, [token, magicLogin, navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
      <div className="w-full max-w-md bg-white rounded-2xl shadow-lg p-8 text-center">
        {status === "loading" && (
          <>
            <Loader2 className="mx-auto h-10 w-10 text-[#0F1E3D] animate-spin mb-4" />
            <h2 className="text-xl font-semibold text-gray-900 mb-1">Logging you in…</h2>
            <p className="text-sm text-gray-500">Please wait while we verify your link.</p>
          </>
        )}

        {status === "success" && (
          <>
            <CheckCircle2 className="mx-auto h-10 w-10 text-green-600 mb-4" />
            <h2 className="text-xl font-semibold text-gray-900 mb-1">Welcome!</h2>
            <p className="text-sm text-gray-500">Redirecting to your dashboard…</p>
          </>
        )}

        {status === "error" && (
          <>
            <AlertTriangle className="mx-auto h-10 w-10 text-red-600 mb-4" />
            <h2 className="text-xl font-semibold text-gray-900 mb-2">Link Expired or Invalid</h2>
            <p className="text-sm text-gray-500 mb-6">{errorMessage}</p>
            <Link
              to="/login"
              className="inline-flex items-center justify-center px-6 py-2.5 bg-[#0F1E3D] text-white rounded-lg text-sm font-medium hover:bg-[#1a2d4f] transition-colors"
            >
              Go to Login
            </Link>
          </>
        )}
      </div>
    </div>
  );
}
